import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/dashboard/shared";
import { getAPIBaseURL, getAdminHeaders } from "@/lib/auth";

type InvitationRange = { min: number; max: number; updated_at: string | null; adjusted?: number };

const ENDPOINT = "/api/v1/admin/invitation-cost";
const CAP = 100000;

export function InvitationPricePage() {
  const queryClient = useQueryClient();
  const current = useQuery({
    queryKey: ["invitation-cost"],
    queryFn: async () => {
      const response = await fetch(`${getAPIBaseURL()}${ENDPOINT}`, { headers: getAdminHeaders() });
      if (!response.ok) throw new Error("Failed to load the invitation price limits");
      return response.json() as Promise<InvitationRange>;
    },
  });

  const [min, setMin] = React.useState("");
  const [max, setMax] = React.useState("");
  React.useEffect(() => {
    if (current.data) {
      setMin(String(current.data.min));
      setMax(String(current.data.max));
    }
  }, [current.data]);

  const minValue = Number(min);
  const maxValue = Number(max);
  const filled = min.trim() !== "" && max.trim() !== "";
  const inBounds = [minValue, maxValue].every((v) => Number.isInteger(v) && v >= 0 && v <= CAP);
  const ordered = minValue <= maxValue;
  const valid = filled && inBounds && ordered;
  const changed = current.data ? minValue !== current.data.min || maxValue !== current.data.max : false;

  const save = useMutation({
    mutationFn: async () => {
      const response = await fetch(`${getAPIBaseURL()}${ENDPOINT}`, {
        method: "PUT",
        headers: { ...getAdminHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ min: minValue, max: maxValue }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error ?? "Save failed");
      return payload as InvitationRange;
    },
    onSuccess: (payload) => {
      const moved = payload.adjusted ?? 0;
      toast.success(
        moved > 0
          ? `Limits saved. ${moved} account${moved === 1 ? "" : "s"} moved inside the new range.`
          : "Limits saved. They apply to everyone from now on.",
      );
      queryClient.invalidateQueries({ queryKey: ["invitation-cost"] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Invitation price"
        description="The lowest and highest number of diamonds a user can ask for an invitation. The same limits apply to everyone, at signup and in M'inviter."
      />

      <Card className="rounded-2xl">
        <CardContent className="flex flex-col gap-2 p-5 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Current limits</p>
            {current.isLoading ? (
              <Skeleton className="mt-2 h-5 w-40" />
            ) : current.isError ? (
              <p className="mt-1 text-sm text-destructive">The current limits could not be loaded.</p>
            ) : (
              <p className="mt-1 text-sm">
                From {current.data?.min} to {current.data?.max} diamonds
              </p>
            )}
          </div>
          {current.data?.updated_at && (
            <p className="text-xs text-muted-foreground">
              Last changed {new Date(current.data.updated_at).toLocaleString("en-GB")}
            </p>
          )}
        </CardContent>
      </Card>

      {!current.isError && (
        <Card className="rounded-2xl">
          <CardContent className="space-y-6 p-6">
            <div className="grid gap-4 md:max-w-lg md:grid-cols-2">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Minimum diamonds</Label>
                <Input inputMode="numeric" value={min} onChange={(e) => setMin(e.target.value.replace(/\D/g, ""))} placeholder="0" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Maximum diamonds</Label>
                <Input inputMode="numeric" value={max} onChange={(e) => setMax(e.target.value.replace(/\D/g, ""))} placeholder="200" />
              </div>
            </div>

            <div className="rounded-xl bg-muted/40 p-4 text-sm text-muted-foreground">
              Set the minimum to 0 to let users receive invitations without diamonds. When you save, anyone whose price
              is below the new minimum or above the new maximum is moved to the nearest limit.
            </div>

            {filled && !inBounds && (
              <p className="text-sm text-destructive">Enter whole numbers between 0 and {CAP.toLocaleString("en-GB")}.</p>
            )}
            {filled && inBounds && !ordered && (
              <p className="text-sm text-destructive">The minimum cannot be higher than the maximum.</p>
            )}

            <Button className="rounded-xl" disabled={!valid || !changed || save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? "Saving…" : "Save limits"}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
